import { t } from "@lingui/macro";
import { useAtomValue } from "jotai";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { AppState, Platform } from "react-native";

import { Button } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import { Box } from "@whoosh/design-system/src/components/primitive";
import {
  getNativeSessionKey,
  onNativeSessionExpiry,
} from "@whoosh/relay/src/private/nativeSessionPersistence";

import { useFlags } from "../../utils/launchDarkly";
import { sessionAtom } from "../Session";
import {
  NativeSessionGateContext,
  NativeSessionReadyContext,
} from "./NativeSessionGateContext";
import {
  getBiometricUnlockEnabled,
  getBiometricUnlockUserId,
  prepareNativeBiometricSession,
  signInWithBiometrics,
} from "./biometricUnlock";

export const BIOMETRIC_RELOCK_INTERVAL_MS = 5 * 60 * 1000;

/** Hide private content without disconnecting screen effects during native session checks. */
export const BiometricSessionGate = ({ children }: PropsWithChildren) => {
  const { biometricAuth } = useFlags();
  const enabled = biometricAuth === true;
  return (
    <BiometricSessionGateContent key={String(enabled)} enabled={enabled}>
      {children}
    </BiometricSessionGateContent>
  );
};

const BiometricSessionGateContent = ({
  children,
  enabled,
}: PropsWithChildren<{ enabled: boolean }>) => {
  const session = useAtomValue(sessionAtom);
  const userId = useRef(session?.user.id);
  userId.current = session?.user.id;
  const [initialized, setInitialized] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [contentVersion, setContentVersion] = useState(0);
  const mounted = useRef(true);
  const checking = useRef(false);
  const authenticating = useRef(false);
  const canUseSession = useRef(false);
  const active = useRef(
    Platform.OS === "web" || AppState.currentState === "active"
  );
  const backgroundedAt = useRef<number | null>(null);
  const relockRequested = useRef(false);
  const foregroundCheckRequested = useRef(false);
  const canUseNativeSession = useCallback(() => canUseSession.current, []);

  const checkSession = useCallback(
    async (initial = false, relock = false) => {
      canUseSession.current = false;
      setReady(false);
      relockRequested.current ||= relock;
      if (checking.current) {
        foregroundCheckRequested.current = true;
        return;
      }
      checking.current = true;
      setFailed(false);
      try {
        if (initial) {
          await prepareNativeBiometricSession(enabled, false);
          if (foregroundCheckRequested.current) initial = false;
        }
        do {
          foregroundCheckRequested.current = false;
          const shouldRelock = relockRequested.current;
          if (!initial && active.current && Platform.OS !== "web") {
            const expectedUserId = userId.current;
            const enrolled = enabled && (await getBiometricUnlockEnabled());
            const sessionKey = await getNativeSessionKey();
            if (!mounted.current) return;
            if ((shouldRelock && enrolled) || !sessionKey) {
              await prepareNativeBiometricSession(enabled, true);
              const enrolledUserId = enrolled
                ? await getBiometricUnlockUserId()
                : null;
              // Resume only this account. Cancellation or a different enrollment
              // returns to the normal login flow with a fresh private tree.
              if (!mounted.current || !active.current) return;
              authenticating.current = true;
              const restored =
                active.current &&
                expectedUserId &&
                enrolledUserId === expectedUserId
                  ? await signInWithBiometrics()
                  : { success: false };
              authenticating.current = false;
              if (!restored.success && mounted.current) {
                if ("error" in restored && restored.error === "unavailable") {
                  setFailed(true);
                  return;
                }
                setContentVersion((current) => current + 1);
              }
            }
          }
          if (active.current && !foregroundCheckRequested.current)
            relockRequested.current = false;
          initial = false;
        } while (active.current && foregroundCheckRequested.current);
        if (mounted.current) {
          setInitialized(true);
          canUseSession.current = active.current;
          setReady(active.current);
        }
      } catch {
        if (mounted.current) setFailed(true);
      } finally {
        authenticating.current = false;
        checking.current = false;
      }
    },
    [enabled]
  );

  useEffect(() => {
    mounted.current = true;
    void checkSession(true);
    if (Platform.OS === "web")
      return () => {
        mounted.current = false;
        canUseSession.current = false;
      };
    const removeExpiryListener = onNativeSessionExpiry(() => {
      relockRequested.current = true;
      canUseSession.current = false;
      setReady(false);
      if (active.current) void checkSession(false, true);
    });
    const subscription = AppState.addEventListener("change", (state) => {
      active.current = state === "active";
      if (!active.current) {
        canUseSession.current = false;
        setReady(false);
        backgroundedAt.current ??= Date.now();
        return;
      }
      const shouldRelock =
        backgroundedAt.current !== null &&
        Date.now() - backgroundedAt.current >= BIOMETRIC_RELOCK_INTERVAL_MS;
      backgroundedAt.current = null;
      // Face ID itself emits inactive/active events. Its exchange already
      // checks the session; do not start another relock behind that prompt.
      if (!checking.current) void checkSession(false, shouldRelock);
      else if (!authenticating.current) {
        foregroundCheckRequested.current = true;
        relockRequested.current ||= shouldRelock;
      }
    });
    return () => {
      mounted.current = false;
      canUseSession.current = false;
      removeExpiryListener();
      subscription.remove();
    };
  }, [checkSession]);

  return (
    <NativeSessionGateContext.Provider value={canUseNativeSession}>
      <NativeSessionReadyContext.Provider value={ready}>
        {initialized ? (
          <Box
            flex={1}
            style={{ display: ready ? "flex" : "none" }}
            key={contentVersion}
          >
            {children}
          </Box>
        ) : null}
        {!ready ? (
          <Box
            flex={1}
            backgroundColor="surface-primary"
            justifyContent="center"
            alignItems="center"
            testID="biometric-session-gate-loader"
          >
            {failed ? (
              <Button
                accessibilityLabel={t`Try again`}
                onPress={() => void checkSession(!initialized)}
              >{t`Try again`}</Button>
            ) : (
              <Loader />
            )}
          </Box>
        ) : null}
      </NativeSessionReadyContext.Provider>
    </NativeSessionGateContext.Provider>
  );
};
